import React, { useMemo } from 'react';
import DashboardModal from '../DashboardModal';

type GeminiRoofRow = {
    id: string;
    label: string;
};

type GeminiResultsTableRow = {
    key: string;
    category: string;
    roofs: GeminiRoofRow[];
};

function mapRoofsForCombo(roofFaceIds: string[], roofCards: any[]): GeminiRoofRow[] {
    return roofFaceIds.map((id: string) => {
        const card = (roofCards as any[]).find((r: any) => r.roofFaceId === id);
        const roofNumRaw = card?.roofNum ?? card?.nameOverride ?? null;
        const roofNum =
            typeof roofNumRaw === 'number' && Number.isFinite(roofNumRaw)
                ? roofNumRaw
                : typeof roofNumRaw === 'string' && roofNumRaw.trim()
                  ? roofNumRaw.trim()
                  : null;
        return {
            id,
            label: roofNum != null ? `Roof ${roofNum}` : id,
        };
    });
}

function buildGeminiResultsTableRows(
    roofCombinationsFromAi: any[] | null | undefined,
    roofCards: any[],
): GeminiResultsTableRow[] {
    if (!roofCombinationsFromAi?.length) return [];
    return roofCombinationsFromAi.map((combo: any, comboIndex: number) => ({
        key: `${combo.name ?? 'combo'}-${comboIndex}`,
        category: combo.name ?? '—',
        roofs: mapRoofsForCombo(combo.roofFaceIds || [], roofCards),
    }));
}

function RoofsCell({ roofs }: { roofs: GeminiRoofRow[] }) {
    if (!roofs.length) {
        return <span className="editor-ai-gemini-results-modal__empty">—</span>;
    }
    return (
        <ul className="editor-ai-gemini-results-modal__roofs">
            {roofs.map((r) => (
                <li key={r.id}>{r.label}</li>
            ))}
        </ul>
    );
}

export interface DashboardGeminiRoofSuggestionsModalProps {
    theme: string;
    open: boolean;
    onClose: () => void;
    roofCombinationsFromAi: any[] | null | undefined;
    roofCards: any[];
}

export function DashboardGeminiRoofSuggestionsModal({
    theme,
    open,
    onClose,
    roofCombinationsFromAi,
    roofCards,
}: DashboardGeminiRoofSuggestionsModalProps) {
    const tableRows = useMemo(
        () => buildGeminiResultsTableRows(roofCombinationsFromAi, roofCards),
        [roofCombinationsFromAi, roofCards],
    );

    return (
        <DashboardModal
            title="Gemini results"
            open={open}
            onClose={onClose}
            theme={theme === 'light' ? 'light' : 'dark'}
            footer={null}
            className="editor-ai-gemini-results-modal"
            rootClassName={`obd-form-controls-modal-root obd-theme-${theme}`}
            destroyOnClose
            width={560}
        >
            <div className="editor-ai-gemini-results-modal__body">
                {tableRows.length === 0 && (
                    <p className="editor-ai-gemini-results-modal__hint">
                        Run AI layouts to see Gemini&apos;s recommended roof groups for each category.
                    </p>
                )}
                {tableRows.length > 0 && (
                    <table className="editor-ai-gemini-results-modal__table">
                        <colgroup>
                            <col className="editor-ai-gemini-results-modal__col-category" />
                            <col className="editor-ai-gemini-results-modal__col-roofs" />
                        </colgroup>
                        <thead>
                            <tr>
                                <th>Category</th>
                                <th>Roofs</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tableRows.map((row) => (
                                <tr key={row.key}>
                                    <td>
                                        <span className="editor-ai-gemini-results-modal__category">
                                            {row.category}
                                        </span>
                                    </td>
                                    <td>
                                        <RoofsCell roofs={row.roofs} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </DashboardModal>
    );
}
